import React, { useEffect, useState } from 'react';
import { DollarSign } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import {
  criarVenda,
  obterComparecimento,
  VendaCreate,
  Venda,
} from '../../services/api.ts';
import BaseModal from './_BaseModal.tsx';
import {
  getAuth,
  nowDateInputs,
  toISOSimpleString,
  maskCurrencyBRL,
  parseBRLToNumber,
} from './_helpers.ts';

interface SaleModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: number | null | undefined;
  comparecimentoId: number | null | undefined;
  contactName: string;
  contactPhone: string;
  clinicId: number;
  onCreated?: (venda: Venda) => void;
}

const SaleModal: React.FC<SaleModalProps> = ({
  isOpen,
  onClose,
  leadId,
  comparecimentoId,
  contactName,
  contactPhone,
  clinicId,
  onCreated,
}) => {
  const { isDark } = useTheme();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    date: '',
    time: '',
    tratamento_fechado: '',
    valor_faturado: '',
    valor_pago: '',
  });

  useEffect(() => {
    if (!isOpen) return;
    const { date, time } = nowDateInputs();
    setForm({ date, time, tratamento_fechado: '', valor_faturado: '', valor_pago: '' });
    setError(null);

    // Pré-preenchimento via dados do comparecimento
    if (!comparecimentoId) return;
    let cancelled = false;
    (async () => {
      try {
        const auth = getAuth(clinicId);
        console.log('[SaleModal] Buscando comparecimento para pré-preenchimento', comparecimentoId);
        const comp = await obterComparecimento(
          auth.effectiveClientId,
          auth.clinicId,
          comparecimentoId
        );
        if (cancelled) return;
        setForm((prev) => ({
          ...prev,
          tratamento_fechado: comp.tratamento_orcado || '',
          valor_faturado: comp.valor_orcamento
            ? maskCurrencyBRL(String(Math.round(comp.valor_orcamento * 100)))
            : '',
        }));
      } catch (err) {
        console.warn('[SaleModal] Falha ao pré-preencher (ignorável):', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, comparecimentoId, clinicId]);

  const isMissingIds = !leadId || !comparecimentoId;
  const canSubmit = !!leadId && !!comparecimentoId && !!form.date && !!form.time;

  const handleSubmit = async () => {
    if (!leadId || !comparecimentoId || !canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const auth = getAuth(clinicId);
      const payload: VendaCreate = {
        lead_id: leadId,
        comparecimento_id: comparecimentoId,
        nome: contactName,
        phone: contactPhone,
        tratamento_fechado: form.tratamento_fechado || undefined,
        valor_faturado: form.valor_faturado ? parseBRLToNumber(form.valor_faturado) : undefined,
        valor_pago: form.valor_pago ? parseBRLToNumber(form.valor_pago) : undefined,
        venda_data: toISOSimpleString(form.date, form.time),
      };
      console.log('[SaleModal] Registrando venda', payload);
      const novaVenda = await criarVenda(auth.clientId, auth.clinicId, payload);
      console.log('[SaleModal] Venda registrada', novaVenda);
      onCreated?.(novaVenda);
      onClose();
    } catch (err: any) {
      console.error('[SaleModal] Erro:', err);
      setError(err?.response?.data?.detail || err?.message || 'Erro ao registrar venda');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Marcar venda"
      subtitle="Registrar fechamento de tratamento"
      icon={DollarSign}
      primaryLabel="Confirmar venda"
      onPrimary={isMissingIds ? undefined : handleSubmit}
      primaryDisabled={!canSubmit}
      saving={saving}
      error={error}
    >
      {isMissingIds ? (
        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          O lead precisa ter um comparecimento registrado antes de marcar a venda.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Data da venda
              </label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className={`w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
            <div className="space-y-2">
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Hora
              </label>
              <input
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                className={`w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Tratamento fechado
            </label>
            <input
              type="text"
              value={form.tratamento_fechado}
              onChange={(e) => setForm({ ...form, tratamento_fechado: e.target.value })}
              placeholder="Ex.: Implante unitário"
              className={`w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              }`}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Valor faturado
              </label>
              <input
                type="text"
                value={form.valor_faturado}
                onChange={(e) => setForm({ ...form, valor_faturado: maskCurrencyBRL(e.target.value) })}
                placeholder="R$ 0,00"
                className={`w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>
            <div className="space-y-2">
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Valor pago
              </label>
              <input
                type="text"
                value={form.valor_pago}
                onChange={(e) => setForm({ ...form, valor_pago: maskCurrencyBRL(e.target.value) })}
                placeholder="R$ 0,00"
                className={`w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>
          </div>
        </>
      )}
    </BaseModal>
  );
};

export default SaleModal;
